/* eslint-disable @typescript-eslint/no-explicit-any -- legacy untyped payloads, read field by field by the settings cards */
import { SdkContext } from '../internal'
import { noteRowFrom, versionedRowWrite } from '@/lib/realtime/rowVersion'

const ROW = 'payoutSettings'

export interface PayoutSettingsStatus {
  status: string
  submittedAt: string | null
  reviewedAt: string | null
  rejectionReason: string | null
  estimatedReviewDate: string | null
  slaDays: number
}

export interface PayoutPrerequisites {
  payoutEnabled: boolean
  /** True as soon as the Business Information is saved, in any status: both are reviewed together. */
  canConfigure: boolean
  businessInformation: {
    status: string
    submittedAt: string | null
    reviewedAt: string | null
    estimatedReviewDate: string | null
  } | null
  /** Why the payout details cannot be filled yet, already translated; null when they can. */
  reason: string | null
}

/** The fields of the submitted record the screen reads. */
export interface SubmittedPayoutSettings {
  status: string
  submittedAt: string | null
  estimatedReviewDate: string | null
}

export class PayoutSettingsResource {
  constructor(private ctx: SdkContext) {}

  async get(): Promise<any> {
    return noteRowFrom(ROW, await this.ctx.call(
      { httpMethod: 'GET', path: '/payout-settings' },
      {}
    ))
  }

  /** Sends the version read (`updatedAt`); a stale save (409) asks before saving over it. */
  async save(payoutData: any): Promise<any> {
    return versionedRowWrite(ROW, (v) => this.ctx.call(
      { httpMethod: 'POST', path: '/payout-settings', hasBody: true },
      { body: { ...payoutData, ...(v ? { updatedAt: v } : {}) } }
    ))
  }

  /** The settings plus the API's own message, which says whether they go to review with the Business Information. */
  async submit(): Promise<{ settings: SubmittedPayoutSettings | null; message: string }> {
    const body = await versionedRowWrite(ROW, (v) => this.ctx.envelope<{ data?: SubmittedPayoutSettings & { updatedAt?: string }; message?: string }>(
      { httpMethod: 'POST', path: '/payout-settings/submit', hasBody: true },
      { body: (v ? { updatedAt: v } : {}) }
    ), (result) => result?.data?.updatedAt)
    return { settings: body?.data ?? null, message: body?.message || '' }
  }

  async getPrerequisites(): Promise<PayoutPrerequisites> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/payout-settings/prerequisites' },
      {}
    )
  }

  async getStatus(): Promise<PayoutSettingsStatus> {
    return this.ctx.call(
      { httpMethod: 'GET', path: '/payout-settings/status' },
      {}
    )
  }
}
